/**
 * Utility function to create a standardized event name for Clobe events.
 *
 * @param name - The base name of the event.
 * @returns A formatted event name prefixed with `[Clobe]`.
 */
export const makeClobeEventName = (name: string) => `[Clobe]: ${name}`;

/**
 * Object containing various event names used in Mixpanel analytics.
 */
export const MIXPANEL_EVENT = {
  GLOBAL: {
    CLICK: 'GlobalClick',
    SEARCH: 'search',
  },
  CERT: {
    VIEW_INSTALL_CODEF: makeClobeEventName('view_install_codef'),
    CLICK_INSTALL_CODEF: makeClobeEventName('click_install_codef'),
  },
  ACCOUNT: {
    CLICK_RENEW_ACCOUNT_LIST: makeClobeEventName('click_renew_account_list'),
    CLICK_EDIT_ACCOUNT_ALIAS: makeClobeEventName('click_edit_account_alias'),
    CLICK_EDIT_ACCOUNT_LIST: makeClobeEventName('click_edit_account_list'),
    CLICK_LABELING_LIST_DRAWER: makeClobeEventName('click_labeling_list_drawer'),
  },
  NAV: {
    ITEM_CLICK: makeClobeEventName('nav_item_click'),
  },
  /**
   * Events related to alarms.
   */
  ALARM: {
    /**
     * Event triggered when creating an alarm.
     * `Event name: create-alarm`
     */
    CREATE: makeClobeEventName('create-alarm'),
  },
  /** G
   * Events related to categories.
   */
  CATEGORY: {
    /**
     * Event triggered when creating a detailed category.
     * `Event name: create-detailed-category`
     */
    CREATE_DETAILED_CATEGORY: makeClobeEventName('create-detailed-category'),
    /**
     * Event triggered when deleting a detailed category.
     * `Event name: delete-detailed-category`
     */
    DELETE_DETAILED_CATEGORY: makeClobeEventName('delete-detailed-category'),
    /*
     * Event triggered when changing the parent category.
     * `Event name: change-parent-category
     * */
    CHANGE_PARENT_CATEGORY: makeClobeEventName('change-parent-category'),
    /**
     * Event triggered when changing the category name.
     * `Event name: change-category-name
     * */
    CHANGE_CATEGORY_NAME: makeClobeEventName('change-category-name'),
  },
  DETAIL: {
    VIEW_DETAILS_DRAWER: makeClobeEventName('view_details_drawer'),
  },
  MEMO: {
    CLICK_EDIT_MEMO: makeClobeEventName('click_edit_memo'),
    CLICK_SAVE_MEMO: makeClobeEventName('click_save_memo'),
    EDIT_MEMO: makeClobeEventName('edit_memo'),
  },
  /**
   * Events related to cash flow forecasts.
   */
  CASHFLOW: {
    /**
     * Event triggered when updating a cash flow forecast.
     * `Event name: UpdateForecast`
     */
    UPDATE_FORECAST: makeClobeEventName('UpdateForecast'),
    CHANGE_ROW_GROUP_TYPE: makeClobeEventName('change_row_group_type'),
    OPEN_TRANSACTIONS: makeClobeEventName('open_transactions'),
    CHANGE_DATE_UNIT: makeClobeEventName('change_date_unit'),
    CHANGE_CURRENCY_UNIT: makeClobeEventName('change_currency_unit'),
  },
  /**
   * Events related to the clipboard.
   */
  CLIPBOARD: {
    /**
     * Event triggered when copying to the clipboard.
     * `Event name: copy-to-clipboard`
     */
    COPY: makeClobeEventName('copy-to-clipboard'),
    /**
     * Event triggered when pasting from the clipboard.
     *  `Event name: paste-from-clipboard`
     */
    PASTE: makeClobeEventName('paste-from-clipboard'),
  },
  /**
   * Events related to downloads.
   */
  DOWNLOAD: {
    /**
     * Event triggered when downloading an Excel file.
     * `Event name: Download`
     */
    DEFAULT: makeClobeEventName('Download'),
  },
  /**
   * Events related to export data.
   */
  EXPORT: {
    /**
     * Event triggered when changing the export range type.
     * `Event name: change_export_range_type`
     */
    CHANGE_RANGE_TYPE: makeClobeEventName('change_export_range_type'),
    /**
     * Event triggered when changing the export date range.
     * `Event name: change_export_date_range`
     */
    CHANGE_DATE_RANGE: makeClobeEventName('change_export_date_range'),
    /**
     * Event triggered when selecting export data type checkbox.
     * `Event name: select_export_data_type`
     */
    SELECT_DATA_TYPE: makeClobeEventName('select_export_data_type'),
    /**
     * Event triggered when clicking the export download button.
     * `Event name: click_export_download`
     */
    CLICK_DOWNLOAD: makeClobeEventName('click_export_download'),
  },
  /**
   * Events related to user interactions with invite codes and banners.
   * `Event name: invite`
   */
  EVENT: {
    /**
     * Event triggered when clicking the copy invite code button.
     * `Event name: click_copy_invite_code`
     */
    CLICK_COPY_INVITE_CODE: 'click_copy_invite_code',
    /**
     * Event triggered when clicking the invite event banner.
     * `Event name: click_invite_event_banner`
     */
    CLICK_INVITE_EVENT_BANNER: 'click_invite_event_banner',
    /**
     * Event triggered when clicking the closing automation banner.
     * `Event name: click_closing_automation_banner`
     */
    CLICK_CLOSING_AUTOMATION_BANNER: 'click_closing_automation_banner',
    /**
     * Event triggered when clicking the referral event banner.
     * `Event name: click_referral_event_banner`
     */
    CLICK_REFERRAL_EVENT_BANNER: makeClobeEventName('click_referral_event_banner'),
    /**
     * Event triggered when clicking the blog review event banner.
     * `Event name: click_blog_review_event_banner`
     */
    CLICK_BLOG_REVIEW_EVENT_BANNER: makeClobeEventName('click_blog_review_event_banner'),
    /**
     * Event triggered when clicking the clobe finance banner.
     * `Event name: click_clobe_finance_banner`
     */
    CLICK_CLOBE_FINANCE_BANNER: makeClobeEventName('click_clobe_finance_banner'),
    /**
     * Event triggered when clicking the open kakao chat banner.
     * `Event name: click_open_kakao_chat_banner`
     */
    CLICK_OPEN_KAKAO_CHAT_BANNER: makeClobeEventName('click_open_kakao_chat_banner'),
  },
  /**
   * Events related to user engagement.
   */
  ENGAGEMENT: {
    /**
     * Event triggered for general engagement.
     * `Event name: Engagement`
     */
    DEFAULT: 'Engagement',
  },
  ESTIMATE_CALCULATE: 'estimate calculate',
  /**
   * Events related to transactions.
   */
  AVAILABLE_CASH_MANAGE: {
    EDIT_ACCOUNT_LIST: makeClobeEventName('edit_account_list'),
    CHANGE_ACCOUNT_SWITCH: makeClobeEventName('change_account_switch'),
    EDIT_ACCOUNT_ALIAS: makeClobeEventName('edit_account_alias'),
  },
  MOBILE: {
    CLICK_AVAILABLE_CASH: makeClobeEventName('click_available_cash'),
    CLICK_CARD_SPENDING: makeClobeEventName('click_card_spending'),
    CLICK_BOTTOM_NAV: makeClobeEventName('click_bottom_nav'),
    CLICK_LIST: makeClobeEventName('click_list'),
    CLICK_FILTER_BUTTON: makeClobeEventName('click_filter_button'),
    SET_FILTER: makeClobeEventName('set_filter'),
    CHANGE_DATE_PERIOD: makeClobeEventName('change_date_period'),

    SWIPE_DATE_CHANGE: makeClobeEventName('swipe_date_change'),
    SCROLL_LOAD_MORE: makeClobeEventName('scroll_load_more'),

    /**
     * Event triggered when the user clicks the '카톡으로 초대하기' button.
     * `Event name: click_mobile_invitation`
     */
    CLICK_MOBILE_INVITATION: makeClobeEventName('click_mobile_invitation'),
    CLICK_BACK: makeClobeEventName('click_back'),
    VIEW_MOBILE_CERTIFICATE_REQUEST: makeClobeEventName('view_mobile_certificate_request'),
  },
  SEARCH: {
    CLICK_RECENT_SEARCH_HISTORY: makeClobeEventName('click_recent_search_history'),
    DELETE_RECENT_SEARCH_HISTORY: makeClobeEventName('delete_recent_search_history'),
    VIEW_SEARCH_RESULT_LIST: makeClobeEventName('view_search_result_list'),
    CLICK_VIEW_MORE_RESULT: makeClobeEventName('click_view_more_result'),
    CLICK_SEARCH_RESULT_TYPE_TAB: makeClobeEventName('click_search_result_type_tab'),
    CLICK_SEARCH_RESULT_ITEM: makeClobeEventName('click_search_result_item'),
  },
  INQUIRE: {
    CLICK_INQUIRE: makeClobeEventName('click_inquire'),
  },
  HOME: {
    CLICK_ACCOUNT_LIST: makeClobeEventName('click_account_list'),
    CLICK_ACCOUNT_LIST_NUDGE: makeClobeEventName('click_account_list_nudge'),
    /**
     * Event triggered when clicking the unseen account detail view button.
     * `Event name: click_unseen_detail_view`
     */
    CLICK_UNSEEN_DETAIL_VIEW: makeClobeEventName('click_unseen_detail_view'),
    /**
     * Event triggered when clicking the cash settings.
     * `Event name: click_cash_settings`
     */
    CLICK_CASH_SETTINGS: makeClobeEventName('click_cash_settings'),
    /**
     * Event triggered when clicking the cash list close.
     * `Event name: click_cash_list_close`
     */
    CLICK_MORE_BUTTON: makeClobeEventName('click_more_button'),
    /**
     * Event triggered when clicking the cash list close.
     * `Event name: click_cash_list_close`
     */
    CLICK_RUNWAY_SETTINGS: makeClobeEventName('click_runway_settings'),
    /**
     * Event triggered when clicking the cash list close.
     * `Event name: click_finance_usecase`
     */
    CLICK_FINANCE_USECASE: makeClobeEventName('click_finance_usecase'),
    /**
     * Event triggered when clicking the cash list close.
     * `Event name: change_finance_simulation_scenario`
     */
    CHANGE_FINANCE_SIMULATION_SCENARIO: makeClobeEventName('change_finance_simulation_scenario'),
    /**
     * Event triggered when clicking the cash list close.
     * `Event name: click_ask_about_finance_reservation`
     */
    CLICK_ASK_ABOUT_FINANCE_RESERVATION: makeClobeEventName('click_ask_about_finance_reservation'),
    /**
     * Event triggered when clicking the cash list close.
     * `Event name: click_ask_about_finance`
     */
    CLICK_ASK_ABOUT_FINANCE: makeClobeEventName('click_ask_about_finance'),
    /**
     * Event triggered when clicking the cash list close.
     * `Event name: click_finance_info`
     */
    CLICK_FINANCE_INFO: makeClobeEventName('click_finance_info'),
    /**
     * Event triggered when clicking the cash list close.
     * `Event name: click_cash_list_close`
     */
    CLICK_TERM_REQUEST: makeClobeEventName('click_term_request'),
    /**
     * Event triggered when clicking the cash list close.
     * `Event name: click_trade_revenue`
     */
    CLICK_TRADE_REVENUE: makeClobeEventName('click_trade_revenue'),
    /**
     * `Event name: click_cash_list_close`
     */
    CLICK_CONNECT_CARD: makeClobeEventName('click_connect_card'),
    /**
     * Event triggered when clicking the cash list close.
     * `Event name: click_cash_list_close`
     */
    CLICK_CARD_UPCOMING_SETTELMENT: makeClobeEventName('click_card_upcoming_settlement'),
    /**
     * Event triggered when clicking the cash list close.
     * `Event name: click_cash_list_close`
     */
    CLICK_CARD_NEW_TAX_INVOICE: makeClobeEventName('click_card_new_tax_invoice'),
    CLICK_CONSULT_RESERVATION: makeClobeEventName('click_consult_reservation'),
    CLICK_TRADE_REVENUE_APPLY: makeClobeEventName('click_home_trade_revenue_apply'),
    CLICK_BANK_TRANSACTION_DRAWER: makeClobeEventName('click_bank_transaction_drawer'),
    CLICK_TAX_INVOICE_DRAWER: makeClobeEventName('click_tax_invoice_drawer'),
    CLICK_CASH_RECEIPT_DRAWER: makeClobeEventName('click_cash_receipt_drawer'),
    CLICK_CARD_APPROVAL_DRAWER: makeClobeEventName('click_card_approval_drawer'),
    CLICK_SIDEBAR_EXPANDED: makeClobeEventName('click_sidebar_expanded'),
    /**
     * Event triggered when clicking a card usage item.
     * `Event name: click_card_usage_item`
     */
    CLICK_CARD_USAGE_ITEM: makeClobeEventName('click_card_usage_item'),
    /**
     * Event triggered when clicking the card scrape error button.
     * `Event name: click_card_scrape_error_button`
     */
    CLICK_CARD_SCRAPE_ERROR_BUTTON: makeClobeEventName('click_card_scrape_error_button'),
    /**
     * Event triggered when clicking the exchange rate view button.
     * `Event name: click_exchange_rate_view`
     */
    CLICK_EXCHANGE_RATE_VIEW: makeClobeEventName('click_exchange_rate_view'),
    /**
     * Event triggered when clicking the close exchange rate box button.
     * `Event name: click_close_exchange_rate_box`
     */
    CLICK_CLOSE_EXCHANGE_RATE_BOX: makeClobeEventName('click_close_exchange_rate_box'),
    /**
     * Event triggered when changing the trend period.
     * `Event name: click_trend_period_change`
     */
    CLICK_TREND_PERIOD_CHANGE: makeClobeEventName('click_trend_period_change'),
    /**
     * Event triggered when clicking the card usage list collapse.
     * `Event name: click_card_usage_list`
     */
    CLICK_CARD_USAGE_LIST: makeClobeEventName('click_card_usage_list'),
    /**
     * Event triggered when clicking the connect bank button.
     * `Event name: click_connect_bank`
     */
    CLICK_CONNECT_BANK: makeClobeEventName('click_connect_bank'),
    /**
     * Event triggered when clicking the custom date change button.
     * `Event name: click_custom_date_change`
     */
    CLICK_CUSTOM_DATE_CHANGE: makeClobeEventName('click_custom_date_change'),
    /**
     * Event triggered when changing the custom date range.
     * `Event name: change_custom_date_range`
     */
    CHANGE_CUSTOM_DATE_RANGE: makeClobeEventName('change_custom_date_range'),
    /**
     * Event triggered when changing the date type (day/week/month/etc).
     * `Event name: change_date_type`
     */
    CHANGE_DATE_TYPE: makeClobeEventName('change_date_type'),
    /**
     * Event triggered when clicking the today/this week/this month/this year button.
     * `Event name: click_today_button`
     */
    CLICK_TODAY_BUTTON: makeClobeEventName('click_today_button'),
    /**
     * Event triggered when clicking the previous date button.
     * `Event name: click_prev_date`
     */
    CLICK_PREV_DATE: makeClobeEventName('click_prev_date'),
    /**
     * Event triggered when clicking the next date button.
     * `Event name: click_next_date`
     */
    CLICK_NEXT_DATE: makeClobeEventName('click_next_date'),
    /**
     * Event triggered when clicking the scrape error button on bank account.
     * `Event name: click_bank_scrape_error_button`
     */
    CLICK_BANK_SCRAPE_ERROR_BUTTON: makeClobeEventName('click_bank_scrape_error_button'),
    /**
     * Event triggered when clicking the account error button.
     * `Event name: click_account_error_button`
     */
    CLICK_ACCOUNT_ERROR_BUTTON: makeClobeEventName('click_account_error_button'),
    /**
     * Event triggered when changing the account type filter in trend chart.
     * `Event name: click_account_type_filter`
     */
    CLICK_ACCOUNT_TYPE_FILTER: makeClobeEventName('click_account_type_filter'),
    /**
     * Event triggered when clicking the daily report banner.
     * `Event name: click_daily_report_banner`
     */
    CLICK_DAILY_REPORT_BANNER: makeClobeEventName('click_daily_report_banner'),
    /**
     * Event triggered when clicking the burn settings save button.
     * `Event name: click_burn_settings_save`
     */
    CLICK_BURN_SETTINGS_SAVE: makeClobeEventName('click_burn_settings_save'),
    /**
     * Event triggered when changing the burn period.
     * `Event name: change_burn_period`
     */
    CHANGE_BURN_PERIOD: makeClobeEventName('change_burn_period'),
    /**
     * Event triggered when selecting the burn base date.
     * `Event name: select_burn_base_date`
     */
    SELECT_BURN_BASE_DATE: makeClobeEventName('select_burn_base_date'),
    /**
     * Event triggered when toggling the exclude hidden accounts switch.
     * `Event name: toggle_exclude_hidden_accounts`
     */
    TOGGLE_EXCLUDE_HIDDEN_ACCOUNTS: makeClobeEventName('toggle_exclude_hidden_accounts'),
    /**
     * Event triggered when checking burn category.
     * `Event name: check_burn_category`
     */
    CHECK_BURN_CATEGORY: makeClobeEventName('check_burn_category'),
    /**
     * Event triggered when opening the export dropdown.
     * `Event name: open_export_dropdown`
     */
    OPEN_EXPORT_DROPDOWN: makeClobeEventName('open_export_dropdown'),
    /**
     * Event triggered when clicking the account manage tab.
     * `Event name: click_account_manage_tab`
     */
    CLICK_ACCOUNT_MANAGE_TAB: makeClobeEventName('click_account_manage_tab'),
    /**
     * Event triggered when clicking the account manage collapse.
     * `Event name: click_account_manage_collapse`
     */
    CLICK_ACCOUNT_MANAGE_COLLAPSE: makeClobeEventName('click_account_manage_collapse'),
    /**
     * Event triggered when toggling account visibility switch.
     * `Event name: toggle_account_visibility`
     */
    TOGGLE_ACCOUNT_VISIBILITY: makeClobeEventName('toggle_account_visibility'),

    NUDGE: {
      CLICK_DISMISS_PNL_SHEET_NUDGE: makeClobeEventName('click_dismiss_pnl_sheet_nudge'),
      CLICK_PNL_SHEET_NUDGE_OK_BUTTON: makeClobeEventName('click_pnl_sheet_nudge_ok_button'),
    },
  },
  TRANSACTION: {
    /**
     * Event triggered when selecting the transaction view type (deposit/withdrawal).
     * `Event name: SelectTransactionViewType`
     */
    SELECT_TRANSACTION_VIEW_TYPE: makeClobeEventName('SelectTransactionViewType'),
    /**
     * Event triggered when changing the date range type in transactions.
     * `Event name: change_transaction_date_range_type`
     */
    CHANGE_DATE_RANGE_TYPE: makeClobeEventName('change_transaction_date_range_type'),
    /**
     * Event triggered when selecting the date range in transactions.
     * `Event name: select_transaction_date_range`
     */
    SELECT_DATE_RANGE: makeClobeEventName('select_transaction_date_range'),
    /**
     * Event triggered when opening the excel download dropdown.
     * `Event name: open_transaction_excel_dropdown`
     */
    OPEN_EXCEL_DROPDOWN: makeClobeEventName('open_transaction_excel_dropdown'),
    /**
     * Event triggered when opening the wehago excel download dropdown.
     * `Event name: open_transaction_wehago_excel_dropdown`
     */
    OPEN_WEHAGO_EXCEL_DROPDOWN: makeClobeEventName('open_transaction_wehago_excel_dropdown'),
    /**
     * Event triggered when mapping and splitting transaction details from a tax invoice.
     * `Event name: TaxInvoice Mapping and Split Transaction`
     */
    MAPPING: makeClobeEventName('TaxInvoice Mapping and Split Transaction'),
    /**
     * Event triggered when clicking the route transactions button.
     * `Event name: click_route_transactions`
     */
    CLICK_ROUTE_TRANSACTIONS: makeClobeEventName('click_route_transactions'),
  },
  GROUP: {
    TRIGER_CREATE: makeClobeEventName('TrigerCreateGroup'),
    CREATE_DETAILED_GROUP: makeClobeEventName('create-detailed-group'),
    DELETE_DETAILED_GROUP: makeClobeEventName('delete-detailed-group'),
    CHANGE_PARENT_GROUP: makeClobeEventName('change-parent-group'),
    CHANGE_GROUP_NAME: makeClobeEventName('change-group-name'),
  },
  /**
   * Events related to labels.
   */
  LABEL: {
    /**
     * Event triggered when click creating a group label.
     * `Event name: clickAddGroup`
     */
    CLICK_ADD_GROUP_LABEL: 'clickAddGroup',
    CLICK_GROUP_ITEM: 'clickGroupItem',
    /**
     * Event triggered when creating a group label.
     * `Event name: create-group-label`
     */
    CREATE_GROUP_LABEL: makeClobeEventName('create-group-label'),
    /**
     * Event triggered when adding a label.
     * `Event name: AddLabel`
     */
    ADD_LABEL: makeClobeEventName('AddLabel'),
    /**
     * Event triggered when double_click_cell
     * `Event name: double_click_cell`
     */
    DOUBLE_CLICK_CELL: makeClobeEventName('double_click_cell'),
    /**
     * Event triggered when click_cell_dropdown.
     *  `Event name: click_cell_dropdown`
     */
    CLICK_CELL_DROPDOWN: makeClobeEventName('click_cell_dropdown'),
    CLICK_SPLIT_BY_TAX_INVOICE: makeClobeEventName('click_split_by_tax_invoice'),
    CLICK_ENTER_REST_AMOUNT: makeClobeEventName('click_enter_rest_amount'),
    CLICK_DELETE_LABEL: makeClobeEventName('click_delete_label'),
    DRAG_MULTISELECT_CELL: makeClobeEventName('drag_multiselect_cell'),
    CLICK_TABLE_FILTER: makeClobeEventName('click_table_filter'),
    CLICK_TABLE_FILTER_OPERATOR: makeClobeEventName('click_table_filter_operator'),
    SEARCH_TABLE_FILTER_VALUE: makeClobeEventName('search_table_filter_value'),
    CLICK_MAPPING_DETAIL: makeClobeEventName('click_mapping_detail'),
    CLICK_ISSUE_MAPPED_TAX_INVOICE: makeClobeEventName('click_issue_mapped_tax_invoice'),
    CLICK_TRANSACTION_DETAIL: makeClobeEventName('click_transaction_detail'),
    EDIT_TRANSACTION_MEMO: makeClobeEventName('edit_transaction_memo'),
    CLICK_FILTER_WITHOUT_LABEL: makeClobeEventName('click_filter_without_label'),
    COPY_CLIPBOARD: makeClobeEventName('copy_clipboard'),
    PASTE_CLIPBOARD: makeClobeEventName('paste_clipboard'),
  },
  /**
   * Events related to date changes.
   */
  DATE_CHANGE: {
    /**
     * Event triggered when changing the date.
     * `Event name: Date Change`
     */
    DEFAULT: makeClobeEventName('Date Change'),
    DATE_UNIT: makeClobeEventName('change_date_unit'),
  },
  /**
   * Events related to scenario creation.
   */
  SCENARIO: {
    /**
     * Event triggered when creating a scenario.
     * `Event name: create-scenario`
     */
    CREATE: makeClobeEventName('create-scenario'),
  },
  NOTIFICATION: {
    CLICK_NOTIFICATION_SETTINGS: makeClobeEventName('click_notification_settings'),
  },
  DAILY_REPORT: {
    CLICK_AI_SUMMARY_TRIGGER: makeClobeEventName('click_ai_summary_trigger'),
    CLICK_AI_SUMMARY: makeClobeEventName('click_ai_summary'),
  },
  /**
   *  Events related to rule List.
   * `Event name: ClickRuleList`
   */
  RULE: {
    LIST: makeClobeEventName('ClickRuleList'),
    CLICK_CREATE_RULE: makeClobeEventName('click_create_rule'),
    /**
     * Event triggered when creating a rule.
     * `Event name: create-rule`
     */
    CREATE: makeClobeEventName('create-rule'),
    /**
     * Event triggered when clicking the labeling panel tab.
     * `Event name: click_labeling_panel_tab`
     */
    CLICK_LABELING_PANEL_TAB: makeClobeEventName('click_labeling_panel_tab'),
    /**
     * Event triggered when clicking the labeling rules button.
     * `Event name: click_button_labeling_rules`
     */
    CLICK_BUTTON_LABELING_RULES: makeClobeEventName('click_button_labeling_rules'),
    /**
     * Event triggered when dragging labeling rules.
     * `Event name: drag_labeling_rules`
     */
    DRAG_LABELING_RULES: makeClobeEventName('drag_labeling_rules'),
    /**
     * Event triggered when clicking create rules button.
     * `Event name: click_button_create_rules`
     */
    CLICK_BUTTON_CREATE_RULES: makeClobeEventName('click_button_create_rules'),
    /**
     * Event triggered when clicking recommended rules card.
     * `Event name: click_card_recom_rules`
     */
    CLICK_CARD_RECOM_RULES: makeClobeEventName('click_card_recom_rules'),
    /**
     * Event triggered when searching rules.
     * `Event name: search_rules`
     */
    CLICK_BUTTON_SEARCH_RULE: makeClobeEventName('click_button_search_rule'),
    /**
     * Event triggered when clicking dismiss recommended rules button.
     * `Event name: click_button_dismiss_recom_rules`
     */
    CLICK_BUTTON_DISMISS_RECOM_RULES: makeClobeEventName('click_button_dismiss_recom_rules'),
    /**
     * Event triggered when clicking more recommended rules button.
     * `Event name: click_button_more_recom_rules`
     */
    CLICK_BUTTON_MORE_RECOM_RULES: makeClobeEventName('click_button_more_recom_rules'),
    /**
     * Event triggered when clicking edit rule title button.
     * `Event name: click_button_edit_rule_title`
     */
    CLICK_BUTTON_EDIT_RULE_TITLE: makeClobeEventName('click_button_edit_rule_title'),
    /**
     * Event triggered when clicking rule field select.
     * `Event name: click_select_rule_field`
     */
    CLICK_SELECT_RULE_FIELD: makeClobeEventName('click_select_rule_field'),
    /**
     * Event triggered when clicking rule operator select.
     * `Event name: click_select_rule_operator`
     */
    CLICK_SELECT_RULE_OPERATOR: makeClobeEventName('click_select_rule_operator'),
    /**
     * Event triggered when clicking rule AND button.
     * `Event name: click_button_rule_and`
     */
    CLICK_BUTTON_RULE_AND: makeClobeEventName('click_button_rule_and'),
    /**
     * Event triggered when clicking rule OR button.
     * `Event name: click_button_rule_or`
     */
    CLICK_BUTTON_RULE_OR: makeClobeEventName('click_button_rule_or'),
    /**
     * Event triggered when clicking add rule filter button.
     * `Event name: click_button_add_rule_filter`
     */
    CLICK_BUTTON_ADD_RULE_FILTER: makeClobeEventName('click_button_add_rule_filter'),
    /**
     * Event triggered when clicking add rule label button.
     * `Event name: click_button_add_rule_label`
     */
    CLICK_BUTTON_ADD_RULE_LABEL: makeClobeEventName('click_button_add_rule_label'),
    /**
     * Event triggered when clicking select rule label button.
     * `Event name: click_button_select_rule_label`
     */
    CLICK_BUTTON_SELECT_RULE_LABEL: makeClobeEventName('click_button_select_rule_label'),
    /**
     * Event triggered when clicking delete rule label button.
     * `Event name: click_button_delete_rule_label`
     */
    CLICK_BUTTON_DELETE_RULE_LABEL: makeClobeEventName('click_button_delete_rule_label'),
    /**
     * Event triggered when clicking save rule button.
     * `Event name: click_button_save_rule`
     */
    CLICK_BUTTON_SAVE_RULE: makeClobeEventName('click_button_save_rule'),
    /**
     * Event triggered when clicking rule more button.
     * `Event name: click_button_rule_more`
     */
    CLICK_BUTTON_RULE_MORE: makeClobeEventName('click_button_rule_more'),
    /**
     * Event triggered when clicking rule activation switch.
     * `Event name: click_switch_rule_activation`
     */
    CLICK_SWITCH_RULE_ACTIVATION: makeClobeEventName('click_switch_rule_activation'),
  },
  FILTER: {
    /**
     * Event triggered when clicking the filter button.
     * `Event name: click_filter`
     */
    CLICK: makeClobeEventName('click_filter'),
    /**
     * Event triggered when applying a filter.
     * `Event name: apply_filter`
     */
    APPLY: makeClobeEventName('apply_filter'),
    /**
     * Event triggered when clicking a filter chip.
     * `Event name: click_filter_chip`
     */
    CLICK_FILTER_CHIP: makeClobeEventName('click_filter_chip'),
  },
  /**
   * Events related to scraping activities.
   */
  SCRAPE: {
    /**
     * Event triggered when executing a scrape.
     * `Event name: Scrape`
     */
    DEFAULT: makeClobeEventName('click_scrape'),
    /**
     * Event triggered when clicking the scrape status button.
     * `Event name: ClickStatusButton`
     */
    STATUS_CLICK: makeClobeEventName('ClickStatusButton'),
    CLICK_REFRESH_CONNECTION: makeClobeEventName('click_refresh_connection'),
  },
  /**
   * Events related to settlement activities.
   */
  SETTLEMENT: {
    TRIGGER: makeClobeEventName('SettlementTrigger'),
    /**
     * Event triggered when updating the settlement deposit date.
     * `Event name: UpdateSettlementDepositDate`
     */
    DATE_CHANGE: makeClobeEventName('UpdateSettlementDepositDate'),
    /**
     * Event triggered when clicking on the settlement status.
     * `Event name: CLICK_SETTLEMENT_STATUS`
     */
    CLICK_STATUS: makeClobeEventName('CLICK_SETTLEMENT_STATUS'),
    /**
     * Event triggered when mapping settlement transactions to tax invoices.
     * `Event name: SettlementTransaction Mapping`
     */
    MAPPING: makeClobeEventName('SettlementTransaction Mapping'),
  },
  /**
   * Events related to user activities.
   */
  USER: {
    /**
     * Event triggered when signing in.
     * `Event name: Signin`
     */
    SIGN_IN: 'Signin',

    /**
     * Event triggered when joining a workspace.
     * `Event name: Join workspace`
     */
    JOIN_WORKSPACE: 'Join workspace',
    /**
     * Event triggered when creating a workspace.
     * `Event name: Create workspace`
     */
    CREATE_WORKSPACE: 'Create workspace',
    INPUT_EMAIL_PASSCODE: makeClobeEventName('input_email_passcode'),
    CONFIRM_EMAIL_PASSCODE: makeClobeEventName('confirm_email_passcode'),
    CLICK_LOGOUT: makeClobeEventName('click_logout'),
    CLICK_SIGNIN: makeClobeEventName('click_signin'),
    CLICK_FORGET_PASSWORD: makeClobeEventName('click_forget_password'),
    CLICK_SIGNUP: makeClobeEventName('click_signup'),
    CLICK_SIGNUP_TRIGGER: makeClobeEventName('click_signup_trigger'),
    CLICK_SEND_MAGIC_LINK: makeClobeEventName('click_send_magic_link'),
    CLICK_INVITE_MEMBER: makeClobeEventName('click_invite_member'),
    CLICK_CONFIRM_INVITE_MEMBER: makeClobeEventName('click_confirm_invite_member'),
    VIEW_USER_SIGNUP: makeClobeEventName('view_user_signup'),
    CHECK_USER_METADATA: makeClobeEventName('check_user_metadata'),
  },
  WORKSPACE: {
    VIEW_SEARCH_BUSINESS: makeClobeEventName('view_search_business'),
    VIEW_CONFIRM_BUSINESS_DETAIL: makeClobeEventName('view_confirm_business_detail'),
    CLICK_CONFIRM_BUSINESS_DETAIL: makeClobeEventName('click_confirm_business_detail'),
    VIEW_CREATE_WORKSPACE: makeClobeEventName('view_create_workspace'),
    CLICK_EDIT_BUSINESS_TO_CREATE: makeClobeEventName('click_edit_business_to_create_workspace'),
    CLICK_VIEW_CORPORATION_SCRAPE_AGREEMENT: makeClobeEventName(
      'click_view_corporation_scrape_agreement',
    ),
    CLICK_CREATE_WORKSPACE: makeClobeEventName('click_create_workspace'),
    CLICK_CREATE_WORKSPACE_FEEDBACK: makeClobeEventName('click_create_workspace_feedback'),
    WORKSPACE_VALIDATION_FAILED: makeClobeEventName('workspace_validation_failed'),
  },
  CERTIFICATE: {
    CLICK_ADD_CERTIFICATE: makeClobeEventName('click_add_certificate'),
    CLICK_DELETE_CERTIFICATE: makeClobeEventName('click_delete_certificate'),
    CLICK_REGISTER_CERTIFICATE: makeClobeEventName('click_register_certificate'),
    CLICK_START_ANALYSIS: makeClobeEventName('click_start_analysis'),
    CLICK_START_ANALYSIS_FEEDBACK: makeClobeEventName('click_start_analysis_feedback'),
    CLICK_CLOSE_ONBOARDING: makeClobeEventName('click_close_onboarding'),
    CLICK_EXPAND_ONBOARDING: makeClobeEventName('click_expand_onboarding'),
  },
  /**
   * Events related to tax invoices.
   */
  TAX_INVOICE: {
    /**
     * Event triggered when adding a Clobe member to Barobill.
     * `Event name: click_add_clobe_member_barobill`
     *
     */
    ADD_CLOBE_MEMBER_TO_BAROBILL: 'click_add_clobe_member_barobill',
    /**
     * Event triggered when signing up for Barobill.
     * `Event name: click_signup_barobill`
     */
    SIGNUP_BAROBILL: 'click_signup_barobill',
    /**
     * Event triggered when issuing a tax invoice.
     * `Event name: IssueTriger`
     */
    ISSUE_TRIGER: makeClobeEventName('IssueTriger'),
    /**
     * Event triggered when issuing a tax invoice.
     * `Event name: Issue`
     */
    ISSUE: makeClobeEventName('Issue'),
    /**
     * Event triggered when modifying an issued tax invoice.
     * `Event name: ModificationIssue`
     */
    MODIFICATION_ISSUE: makeClobeEventName('ModificationIssue'),
    /**
     * Event triggered when clicking the duplicate issue button.
     * `Event name: click_duplicate_issuance`
     */
    CLICK_DUPLICATE_ISSUE: makeClobeEventName('click_duplicate_issuance'),
    /**
     * Event triggered when clicking the continue issue button.
     * `Event name: click_continue_issuance`
     */
    CLICK_CONTINUE_ISSUE: makeClobeEventName('click_continue_issuance'),
  },
  /**
   * Events related to financial activities.
   */
  FINANCE: {
    /**
     * Event triggered when clicking the term request nudge banner.
     * `Event name: Click Term Request Nudge Banner`
     */
    CLICK_TERM_REQUEST_NUDGE_BANNER: 'Click Term Request Nudge Banner',
    CLICK_TRADE_REVENUE_APPLY: makeClobeEventName('click_finance_trade_revenue_apply'),
    CLICK_NAV_BANNER: makeClobeEventName('click_finance_nav_banner'),
  },
  TRADE_REVENUE: {
    CLICK_TRADE_REVENUE_APPLY: makeClobeEventName('click_trade_dashboard_trade_revenue_apply'),
    CLICK_TRADE_REVENUE_COMPLETE: makeClobeEventName(
      'click_trade_dashboard_trade_revenue_complete',
    ),
    CLICK_PERIOD_CHANGE: makeClobeEventName('click_trade_revenue_period_change'),
    CLICK_DIRECT_INPUT_PAYOUT: makeClobeEventName('click_trade_revenue_direct_input_payout'),
    CLICK_TAX_ACCOUNTING_INFO: makeClobeEventName('click_trade_revenue_tax_accounting_info'),
    CLICK_CONSULT_RESERVATION: makeClobeEventName('click_trade_revenue_consult_reservation'),
  },
  /**
   * Events related to various view actions.
   * `Event name: View`
   */
  VIEW: {
    /**
     * Event triggered for a default page view.
     * `Event name: Pageview`
     */
    DEFAULT: 'Pageview',
    /**
     * Event triggered for a Clobe-specific page view.
     * `Event name: View`
     */
    CLOBE_PAGE: makeClobeEventName('View'),
    /**
     * Event triggered when viewing the tax invoice mapping and split transaction modal.
     * `Event name: View TaxInvoice Mapping Split Transaction Modal`
     */
    TAXINVOICE_MAPPING_AND_SPLIT_MODAL: makeClobeEventName(
      'View TaxInvoice Mapping Split Transaction Modal',
    ),
    /**
     * Event triggered when viewing the settlement list drawer.
     * `Event name: View Settlement List Drawer`
     */
    SETTLEMENT_LIST_DRAWER: 'View Settlement List Drawer',
    /**
     * Event triggered when viewing the settlement transaction mapping modal.
     * `Event name: View SettlementTransaction Mapping Modal`
     */
    SETTLEMENT_MAPPING_MODAL: makeClobeEventName('View SettlementTransaction Mapping Modal'),
  },
  PARTNERS: {
    /**
     * Event triggered for a Partners Intro Page view.
     * `Event name: View Partners Intro Page`
     */
    PARTNERS_INTRO_PAGE: makeClobeEventName('view_partners_intro_page'),
    CLICK_PARTNERS_START_BUTTON: makeClobeEventName('click_partners_start_button'),
    /**
     * Event triggered when clicking the invite partner button in modal.
     * `Event name: Click Invite Partner Button`
     */
    CLICK_INVITE_PARTNER_BUTTON: makeClobeEventName('click_invite_partner_button'),
    /**
     * Event triggered when clicking the copy partner link button in modal.
     * `Event name: Click Copy Partner Link`
     */
    CLICK_COPY_PARTNER_LINK: makeClobeEventName('click_copy_partner_link'),
    /**
     * Event triggered when clicking the partner event form button in modal.
     * `Event name: Click Partner Event Form`
     */
    CLICK_PARTNER_EVENT_FORM: makeClobeEventName('click_partner_event_form'),
    /**
     * Event triggered when clicking the dismiss intro modal button.
     * `Event name: click_dismiss_intro_modal`
     */
    CLICK_DISMISS_INTRO_MODAL: makeClobeEventName('click_dismiss_intro_modal'),
  },
  ANALYSIS: {
    CREATE: makeClobeEventName('create-analysis'),
    RENAME_ANALYSIS: makeClobeEventName('rename-analysis'),
    DUPLICATE_ANALYSIS: makeClobeEventName('duplicate-analysis'),
    DELETE_ANALYSIS: makeClobeEventName('delete-analysis'),
    SAVE_CHANGES_ANALYSIS: makeClobeEventName('save-changes-analysis'),
    OPEN_SETTINGS_PANEL_ANALYSIS: makeClobeEventName('open-settings-panel-analysis'),
    CLOSE_SETTINGS_PANEL_ANALYSIS: makeClobeEventName('close-settings-panel-analysis'),
    ADD_ROW_GROUP_ANALYSIS: makeClobeEventName('add-row-group-analysis'),
    ADD_COLUMN_GROUP_ANALYSIS: makeClobeEventName('add-column-group-analysis'),
    DELETE_ROW_GROUP_ANALYSIS: makeClobeEventName('delete-row-group-analysis'),
    DELETE_COLUMN_GROUP_ANALYSIS: makeClobeEventName('delete-column-group-analysis'),
    TRANSPOSE_ROW_COLUMN_ANALYSIS: makeClobeEventName('transpose-row-column-analysis'),
    SWITCH_LABEL_DEPTH_ANALYSIS: makeClobeEventName('switch-label-depth-analysis'),
    PRESET_CLICK: makeClobeEventName('click-preset-analysis'),
  },
  WIRING: {
    /**
     * Event triggered when clicking the wire transfer button.
     * `Event name: click_wire_transfer`
     */
    CLICK_WIRE_TRANSFER: makeClobeEventName('click_wire_transfer'),
    /**
     * Event triggered when requesting a wire transfer.
     * `Event name: request_wire_transfer`
     */
    REQUEST_WIRE_TRANSFER: makeClobeEventName('request_wire_transfer'),
    /**
     * Event triggered when executing a wire transfer.
     * `Event name: execute_wire_transfer`
     */
    EXECUTE_WIRE_TRANSFER: makeClobeEventName('execute_wire_transfer'),
  },
  CARD: {
    MAP_CARD_TRANSACTION: makeClobeEventName('MapCardTransaction'),
  },
  ONBOARDING: {
    CLICK_HELP_IN_CERTIFICATE_REGISTER: makeClobeEventName('click_help_in_certificate_register'),
  },
  PRIVACY: {
    OPEN_NOTICE_CHANGE_MODAL: makeClobeEventName('open_privacy_notice_change_modal'),
    CLOSE_NOTICE_CHANGE_MODAL: makeClobeEventName('close_privacy_notice_change_modal'),
    TOGGLE_DO_NOT_SHOW_AGAIN: makeClobeEventName('toggle_privacy_notice_do_not_show_again'),
  },
  /**
   * Events related to loan activities.
   */
  LOAN: {
    /**
     * Event triggered when clicking to request loan limit inquiry.
     * `Event name: click_loan_limit_inquiry`
     */
    CLICK_LOAN_LIMIT_INQUIRY: makeClobeEventName('click_loan_limit_inquiry'),
    /**
     * Event triggered when clicking to apply for a loan.
     * `Event name: click_loan_apply`
     */
    CLICK_LOAN_APPLY: makeClobeEventName('click_loan_apply'),
    /**
     * Event triggered when routing to the DAOL loan application page.
     * `Event name: route_daol_loan_application`
     */
    ROUTE_DAOL_LOAN_APPLICATION: makeClobeEventName('route_daol_loan_application'),
  },
  /**
   * Events related to scrape floating panel.
   */
  PNL: {
    OPEN_DATE_RANGE: makeClobeEventName('pnl_open_date_range'),
    OPEN_BUSINESS_PARTNER: makeClobeEventName('pnl_open_business_partner'),
    OPEN_EVIDENCE_ROW_GROUP: makeClobeEventName('pnl_open_evidence_row_group'),
    OPEN_LEDGER_ROW_GROUP: makeClobeEventName('pnl_open_ledger_row_group'),
    SELECT_DATE_RANGE: makeClobeEventName('pnl_select_date_range'),
    CLICK_INVITE_TAX_AGENT_BANNER: makeClobeEventName('pnl_click_invite_tax_agent_banner'),
    CLICK_INVITE_TAX_AGENT_BUTTON: makeClobeEventName('pnl_click_invite_tax_agent_button'),
    CLICK_EVIDENCE_CELL: makeClobeEventName('pnl_click_evidence_cell'),
    CLICK_LEDGER_CELL: makeClobeEventName('pnl_click_ledger_cell'),
    CLOSE_EVIDENCE_DETAIL_PANEL: makeClobeEventName('pnl_close_evidence_detail_panel'),
    CHANGE_EVIDENCE_DETAIL_TAB: makeClobeEventName('pnl_change_evidence_detail_tab'),
    CLOSE_LEDGER_DETAIL_PANEL: makeClobeEventName('pnl_close_ledger_detail_panel'),
    SELECT_BUSINESS_PARTNER: makeClobeEventName('pnl_select_business_partner'),
    SELECT_EVIDENCE_DETAIL_ROW: makeClobeEventName('pnl_select_evidence_detail_row'),
    SELECT_LEDGER_DETAIL_ROW: makeClobeEventName('pnl_select_ledger_detail_row'),
  },
  SCRAPE_FLOATING_PANEL: {
    /**
     * Event triggered when viewing the scrape floating panel.
     * `Event name: view_scrape_floating_panel`
     */
    VIEW: makeClobeEventName('view_scrape_floating_panel'),
    /**
     * Event triggered when closing the scrape floating panel.
     * `Event name: close_scrape_floating_panel`
     */
    CLOSE: makeClobeEventName('close_scrape_floating_panel'),
    /**
     * Event triggered when minimizing the scrape floating panel.
     * `Event name: minimize_scrape_floating_panel`
     */
    MINIMIZE: makeClobeEventName('minimize_scrape_floating_panel'),
    /**
     * Event triggered when expanding the scrape floating panel.
     * `Event name: expand_scrape_floating_panel`
     */
    EXPAND: makeClobeEventName('expand_scrape_floating_panel'),
    /**
     * Event triggered when clicking the detail button in the scrape floating panel.
     * `Event name: click_detail_button_in_scrape_floating_panel`
     */
    CLICK_DETAIL: makeClobeEventName('click_detail_button_in_scrape_floating_panel'),
  },
};
